import { API_BASE, getAuthHeaders, getSupabaseClient } from '../../utils/supabase/client';

type AccessLink = { accessUrl: string; expiresAt: number };
type PendingLink = { resolve: (url: string) => void; reject: (reason: Error) => void };
const PREFIX = 'quantis:avatar-link:';
const cache = new Map<string, AccessLink>();
const inflight = new Map<string, Promise<string>>();
const pending = new Map<string, Map<string, PendingLink>>();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isStoredAvatarId(id: string): boolean { return UUID.test(id); }

function readLink(key: string): AccessLink | null {
  let value = cache.get(key);
  if (!value) {
    try { value = JSON.parse(sessionStorage.getItem(key) || 'null') as AccessLink | undefined; } catch { /* Storage may be disabled. */ }
  }
  if (!value || typeof value.accessUrl !== 'string' || !Number.isFinite(value.expiresAt)
    || value.expiresAt <= Date.now() + 60_000) return null;
  cache.set(key, value);
  return value;
}

export function invalidateStoredAvatarAccessUrl(id: string): void {
  for (const key of cache.keys()) if (key.endsWith(`:${id}`)) cache.delete(key);
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(PREFIX) && key.endsWith(`:${id}`)) sessionStorage.removeItem(key);
    }
  } catch { /* Continue with memory caching if storage is disabled. */ }
}

async function flushLinks(userId: string): Promise<void> {
  const queue = pending.get(userId);
  pending.delete(userId);
  if (!queue) return;
  const items = [...queue.entries()];
  // Bound requests to the server's 100-avatar limit on larger team screens.
  await Promise.all(Array.from({ length: Math.ceil(items.length / 100) }, async (_, page) => {
    const batch = items.slice(page * 100, (page + 1) * 100);
    try {
      const session = await getSupabaseClient().auth.getSession();
      if (session.data.session?.user.id !== userId) throw new Error('Avatar session changed');
      const response = await fetch(`${API_BASE}/files/avatar-access-links`, {
        method: 'POST', headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ids: batch.map(([id]) => id) }),
      });
      if (!response.ok) throw new Error(`Avatar access unavailable: ${response.status}`);
      const body = await response.json();
      const current = await getSupabaseClient().auth.getSession();
      if (current.data.session?.user.id !== userId) throw new Error('Avatar session changed');
      for (const [id, item] of batch) {
        const link = body.links?.[id];
        const expiresAt = Date.parse(link?.expiresAt);
        if (typeof link?.accessUrl !== 'string' || !Number.isFinite(expiresAt) || expiresAt <= Date.now() + 60_000) {
          item.reject(new Error('Avatar is no longer available'));
          continue;
        }
        const key = `${PREFIX}${userId}:${id}`;
        const value = { accessUrl: link.accessUrl, expiresAt };
        cache.set(key, value);
        try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Memory cache still works. */ }
        item.resolve(value.accessUrl);
      }
    } catch (error) {
      for (const [, item] of batch) item.reject(error instanceof Error ? error : new Error('Avatar access unavailable'));
    }
  }));
}

/** Signed URLs stay private, expire normally and are scoped to the signed-in user. */
export async function resolveStoredAvatarAccessUrl(id: string): Promise<string> {
  const session = await getSupabaseClient().auth.getSession();
  const userId = session.data.session?.user.id;
  if (!userId) throw new Error('Sign in to view avatars');
  const key = `${PREFIX}${userId}:${id}`;
  const cached = readLink(key);
  if (cached) return cached.accessUrl;
  const existing = inflight.get(key);
  if (existing) return existing;
  const request = new Promise<string>((resolve, reject) => {
    let queue = pending.get(userId);
    if (!queue) {
      queue = new Map();
      pending.set(userId, queue);
      setTimeout(() => { void flushLinks(userId); }, 0);
    }
    queue.set(id, { resolve, reject });
  });
  inflight.set(key, request);
  try { return await request; } finally { inflight.delete(key); }
}
